import axios from "axios";

// Web talks to the backend at REACT_APP_BACKEND_URL + /api. When that env var
// is empty (production build bundled inside the backend), we fall back to a
// same-origin relative "/api" so the SPA hits whatever domain served it.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "fdc_token";
export const USER_KEY = "fdc_user";

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 45000,
});

// Attach the bearer token on every request.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto sign-out on a dead session. Business 401/403s on feature endpoints
// are NOT treated as auth failures (mirrors the mobile client contract).
let handlingAuth = false;
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    const rawDetail = error?.response?.data?.detail;
    const detail = (typeof rawDetail === "string" ? rawDetail : rawDetail?.message || "").toLowerCase();

    const isLoginReq = /\/(auth\/login|auth\/register|companies\/register|auth\/client-register)/.test(url);
    const isAuthScoped = /\/auth\/(me|refresh)\b/.test(url);
    const looksLikeAuth =
      detail.includes("invalid token") ||
      detail.includes("token has expired") ||
      detail.includes("session revoked") ||
      detail.includes("user not found") ||
      detail.includes("not authenticated") ||
      detail.includes("could not validate credentials");

    const isAuthError =
      !isLoginReq && (status === 401 || status === 403) && (isAuthScoped || looksLikeAuth);

    if (isAuthError && !handlingAuth) {
      handlingAuth = true;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setTimeout(() => {
        handlingAuth = false;
        const p = window.location.pathname;
        if (!p.startsWith("/login") && !p.startsWith("/portal/login")) {
          window.location.href = "/login";
        }
      }, 120);
    }

    // Plan-limit (402) → surface the global "Upgrade to unlock" dialog with the
    // structured code/message. `trial_view_only` is excluded because the report
    // flow shows its own framed view-only preview instead of a modal.
    if (status === 402) {
      const fire = (data) => {
        const code = data?.code;
        if (code && code !== "trial_view_only") {
          error.planLimit = data; // let local catch blocks skip a redundant toast
          try {
            window.dispatchEvent(new CustomEvent("fdc:plan-limit", { detail: data }));
          } catch { /* ignore */ }
        }
      };
      const data = error?.response?.data;
      // Blob-typed requests (e.g. .xlsx exports with responseType:"blob") deliver
      // the JSON error body as a Blob, so parse it before reading the code.
      if (data && typeof Blob !== "undefined" && data instanceof Blob) {
        return data.text().then((txt) => {
          let parsed = {};
          try { parsed = JSON.parse(txt); } catch { /* ignore */ }
          if (error.response) error.response.data = parsed;
          fire(parsed);
          return Promise.reject(error);
        });
      }
      fire(data);
    }
    return Promise.reject(error);
  }
);

// ── Domain modules — mirror the mobile /src/services/api contract ──────────
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  me: () => api.get("/auth/me"),
  register: (data) => api.post("/auth/register", data),
  clientRegister: (data) => api.post("/auth/client-register", data),
  deleteAccount: (data) => api.delete("/auth/users/me", { data }),
  exportMyData: () => api.get("/account/export"),
  registerCompany: (data) => api.post("/companies/register", data),
  registerContractor: (data) => api.post("/marketplace/contractors/register", data),
};

export const buildingsApi = {
  getAll: () => api.get("/buildings"),
  getOne: (id) => api.get(`/buildings/${id}`),
  completionSummary: (id) => api.get(`/buildings/${id}/completion-summary`),
  create: (data) => api.post("/buildings", data),
  update: (id, data) => api.put(`/buildings/${id}`, data),
  remove: (id) => api.delete(`/buildings/${id}`),
  remedialTracking: (id) => api.get(`/buildings/${id}/remedial-tracking`),
  upsertRemedialTracking: (id, doorId, defectKey, data) =>
    api.put(`/buildings/${id}/remedial-tracking/${doorId}/${encodeURIComponent(defectKey)}`, data),
  resyncImports: (id) => api.post(`/buildings/${id}/resync-imports`),
  duplicateDoors: (id) => api.get(`/buildings/${id}/duplicate-doors`),
  removeDuplicateDoors: (id, doorIds) => api.post(`/buildings/${id}/remove-duplicate-doors`, { door_ids: doorIds }),
  // Iter-252 — merge duplicate buildings (reversible soft-archive).
  archived: () => api.get("/buildings/archived"),
  merge: (keeperId, sourceId) => api.post(`/buildings/${keeperId}/merge`, { source_building_id: sourceId }),
  unmerge: (sourceId) => api.post(`/buildings/${sourceId}/unmerge`),
  // Iter-253 — bulk-assign a client (managing agent) to doors within a building.
  assignClient: (buildingId, body) => api.post(`/buildings/${buildingId}/doors/assign-client`, body),
};

// Iter-252 — property-management companies + per-door PM/Private assignment.
export const propertyManagementApi = {
  list: () => api.get("/property-management-companies"),
  create: (data) => api.post("/property-management-companies", data),
  update: (id, data) => api.put(`/property-management-companies/${id}`, data),
  remove: (id) => api.delete(`/property-management-companies/${id}`),
  assignDoors: (buildingId, body) => api.post(`/buildings/${buildingId}/doors/assign-management`, body),
};

export const doorsApi = {
  getAll: (buildingId, opts = {}) => api.get("/doors", { params: { ...(buildingId ? { building_id: buildingId } : {}), ...(opts.includePhotos === false ? { include_photos: false } : {}) } }),
  getOne: (id) => api.get(`/doors/${id}`),
  create: (data) => api.post("/doors", data),
  update: (id, data) => api.put(`/doors/${id}`, data),
  remove: (id) => api.delete(`/doors/${id}`),
  verifyRepair: (id, data) => api.post(`/doors/${id}/verify-repair`, data),
  markLabelsPrinted: (ids) => api.post("/doors/label-printed", { door_ids: ids }),
};

export const inspectionsApi = {
  getAll: (params) => api.get("/inspections", { params }),
  getOne: (id) => api.get(`/inspections/${id}`),
  create: (data) => api.post("/inspections", data),
  update: (id, data) => api.put(`/inspections/${id}`, data),
  remove: (id) => api.delete(`/inspections/${id}`),
  restore: (id) => api.post(`/inspections/${id}/restore`),
  move: (id, doorId) => api.post(`/inspections/${id}/move`, { door_id: doorId }),
  due: (params) => api.get("/inspection-schedule/due", { params }),
};

export const statisticsApi = {
  get: () => api.get("/statistics"),
  pendingReinspections: () => api.get("/pending-reinspections"),
};

export const quotesApi = {
  list: (params) => api.get("/quotes", { params }),
  getOne: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post("/quotes", data),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  send: (id, data = {}) => api.post(`/quotes/${id}/send`, data),
  decision: (id, data) => api.post(`/quotes/${id}/decision`, data),
  duplicate: (id) => api.post(`/quotes/${id}/duplicate`),
  remove: (id) => api.delete(`/quotes/${id}`),
  doorPhotos: (id) => api.get(`/quotes/${id}/door-photos`),
  generateInvoice: (id, data = {}) => api.post(`/quotes/${id}/generate-invoice`, data),
  // Generators (parity with mobile) — build quotes from failed inspections.
  autoFromInspection: (inspectionId, contractorId) =>
    api.post(`/quotes/auto-from-inspection/${inspectionId}`, null, {
      params: contractorId ? { contractor_id: contractorId } : {},
    }),
  autoFromBuilding: (buildingId) => api.post(`/quotes/auto-from-building/${buildingId}`),
  failedComponents: (doorId) => api.get(`/quotes/failed-components/${doorId}`),
  inspectionContext: (inspectionId) => api.get(`/quotes/inspection-context/${inspectionId}`),
  regenerate: (id) => api.post(`/quotes/${id}/regenerate`),
  convertToNewDoor: (id, payload) => api.post(`/quotes/${id}/convert-to-new-door`, payload),
  applyPrice: (id, payload) => api.post(`/quotes/${id}/apply-price`, payload),
  // Post-work inspection & compliance report add-on (one line per door).
  setReinspection: (id, enabled, opts = {}) =>
    api.post(`/quotes/${id}/reinspection`, { enabled, ...opts }),
};

export const jobTemplatesApi = {
  list: () => api.get("/job-templates"),
  create: (data) => api.post("/job-templates", data),
  update: (id, data) => api.put(`/job-templates/${id}`, data),
  remove: (id) => api.delete(`/job-templates/${id}`),
};

export const invoicesApi = {
  list: (params) => api.get("/invoices", { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post("/invoices", data),
  update: (id, data) => api.patch(`/invoices/${id}`, data),
  remind: (id) => api.post(`/invoices/${id}/remind`, {}),
  remove: (id) => api.delete(`/invoices/${id}`),
};

export const savedReportsApi = {
  list: (params) => api.get("/saved-reports", { params }),
  download: (id) => api.get(`/saved-reports/${id}/download`),
  create: (data) => api.post("/saved-reports", data),
  remove: (id) => api.delete(`/saved-reports/${id}`),
  setStatus: (id, data) => api.patch(`/saved-reports/${id}/status`, data),
};

export const companiesApi = {
  me: () => api.get("/companies/me"),
  update: (data) => api.put("/companies/me", data),
  rotateCode: () => api.post("/companies/rotate-code", {}),
};

export const usersApi = {
  list: () => api.get("/users"),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data || {}),
  remove: (id) => api.delete(`/users/${id}`),
};

export const reportTemplatesApi = {
  get: (reportType) => api.get("/report-templates", reportType ? { params: { report_type: reportType } } : undefined),
  update: (data, reportType) => api.put("/report-templates", data, reportType ? { params: { report_type: reportType } } : undefined),
};

// Data backup & restore (admin-only) — mirrors mobile settings/data-backup.
export const backupsApi = {
  list: () => api.get("/backups"),
  getOne: (id) => api.get(`/backups/${id}`),
  create: () => api.post("/backups", {}),
  remove: (id) => api.delete(`/backups/${id}`),
  restore: (payload) => api.post("/backups/import", payload),
};

export const autoBackupApi = {
  get: () => api.get("/settings/auto-backup"),
  update: (patch) => api.put("/settings/auto-backup", patch),
  runNow: () => api.post("/settings/auto-backup/run-now", {}),
};

export const pricingApi = {
  list: (params) => api.get("/pricing", { params }),
  create: (data) => api.post("/pricing", data),
  update: (id, data) => api.put(`/pricing/${id}`, data),
};

export const platformApi = {
  stats: () => api.get("/platform/stats"),
  companies: (params) => api.get("/platform/companies", { params }),
  company: (id) => api.get(`/platform/companies/${id}`),
  plans: () => api.get("/platform/plans"),
  updatePlans: (data) => api.put("/platform/plans", data),
  changePlan: (id, data) => api.put(`/platform/companies/${id}/plan`, data),
  suspend: (id, data = {}) => api.post(`/platform/companies/${id}/suspend`, data),
  unsuspend: (id) => api.post(`/platform/companies/${id}/unsuspend`, {}),
  archive: (id, data) => api.post(`/platform/companies/${id}/archive`, data),
  unarchive: (id) => api.post(`/platform/companies/${id}/unarchive`, {}),
  seatLimit: (id, data) => api.put(`/platform/companies/${id}/seat-limit`, data),
  startTrial: (id, data) => api.post(`/platform/companies/${id}/trial`, data),
  promote: (userId) => api.post(`/platform/users/${userId}/promote`, {}),
  resetUserPassword: (userId, data) => api.post(`/platform/users/${userId}/reset-password`, data || {}),
  audit: (params) => api.get("/platform/audit", { params }),
  impersonate: (userId) => api.post(`/platform/users/${userId}/impersonate`, {}),
  companyBuildings: (id) => api.get(`/platform/companies/${id}/buildings`),
  buildingDoors: (id) => api.get(`/platform/buildings/${id}/doors`),
  doorInspections: (id) => api.get(`/platform/doors/${id}/inspections`),
  paymentsChecklist: () => api.get("/platform/payments-checklist"),
  togglePaymentsChecklist: (body) => api.put("/platform/payments-checklist", body),
  discounts: () => api.get("/platform/discounts"),
  createDiscount: (data) => api.post("/platform/discounts", data),
  revokeDiscount: (id) => api.delete(`/platform/discounts/${id}`),
};

// Tenant-facing subscription / billing (self-serve for company admins).
export const subscriptionApi = {
  get: () => api.get("/subscription"),
  history: () => api.get("/subscription/history"),
  seats: () => api.get("/subscription/seats"),
  seatsPreview: (seats) => api.get("/subscription/seats/preview", { params: { seats } }),
  updateSeats: (seats) => api.post("/subscription/seats", { seats }),
};

// Legal & Data-Protection — versioned Privacy Policy + Terms of Use.
export const legalApi = {
  documents: () => api.get("/legal/documents"),
  document: (type) => api.get(`/legal/documents/${type}`),
  accept: () => api.post("/legal/accept"),
  dataInventory: () => api.get("/legal/data-inventory"),
  retentionPreview: (overrides) => api.post("/legal/retention/preview", { retention_overrides: overrides || null }),
  retentionPurge: (categories, overrides) => api.post("/legal/retention/purge", { categories, confirm: true, retention_overrides: overrides || null }),
};

// Stripe web card subscriptions.
export const billingApi = {
  checkout: (data) => api.post("/billing/checkout", data),
  status: (sessionId) => api.get(`/billing/status/${sessionId}`),
  portal: (data) => api.post("/billing/portal", data),
};

// Super-admin data-health tools (mirrors mobile admin/data-health screen).
export const dataHealthApi = {
  backfillEmptyItems: (opts = {}) =>
    api.post("/maintenance-jobs/admin/backfill-empty-items", null, {
      params: {
        dry_run: opts.dry_run ?? true,
        notes_regex: opts.notes_regex || undefined,
        limit: opts.limit ?? 500,
      },
      timeout: 60000,
    }),
  recomputeDoorStatuses: (opts = {}) =>
    api.post("/doors/admin/recompute-statuses", null, {
      params: {
        dry_run: opts.dry_run ?? true,
        limit: opts.limit ?? 5000,
      },
      timeout: 120000,
    }),
};

export const contractorsApi = {
  list: () => api.get("/contractors"),
  get: (id) => api.get(`/contractors/${id}`),
  create: (data) => api.post("/contractors", data),
  update: (id, data) => api.put(`/contractors/${id}`, data),
  remove: (id) => api.delete(`/contractors/${id}`),
};

export const maintenanceApi = {
  list: (params) => api.get("/maintenance-jobs", { params }),
  getOne: (id) => api.get(`/maintenance-jobs/${id}`),
  // Create job(s) from an accepted quote (splits per door by default).
  create: (data) => api.post("/maintenance-jobs", data),
  createAdHoc: (data) => api.post("/maintenance-jobs/ad-hoc", data),
  // AI remedial job-sheet extraction — upload a supplier quote / 3rd-party
  // inspection report; backend Gemini pipeline returns prefill data.
  extractFromFile: (formData) =>
    api.post("/maintenance-jobs/extract-from-file", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }),
  update: (id, data) => api.patch(`/maintenance-jobs/${id}`, data),
  remove: (id) => api.delete(`/maintenance-jobs/${id}`),
  // Engineer field workflow (mirrors mobile): start the job, then submit
  // the completion (per-item photos, materials, signature) to mark it done.
  start: (id, via) => api.put(`/maintenance-jobs/${id}/start${via ? `?via=${via}` : ""}`),
  complete: (id, data) => api.put(`/maintenance-jobs/${id}/complete`, data),
  failedDoorsInBuilding: (buildingId) =>
    api.get(`/maintenance-jobs/failed-doors-in-building/${buildingId}`),
  bulkFromFailedDoors: (payload) =>
    api.post("/maintenance-jobs/bulk-from-failed-doors", payload),
  // Flag the job's door for post-work re-inspection (adds it to the
  // admin "Doors awaiting re-inspection" queue). Callable by the
  // assigned engineer or any admin/inspector.
  requestReinspection: (id) =>
    api.post(`/maintenance-jobs/${id}/request-reinspection`),
  completeEmergency: (id, payload) =>
    api.post(`/maintenance-jobs/${id}/complete-emergency`, payload),
};

export const engineersApi = {
  list: () => api.get("/engineers"),
  create: (data) => api.post("/engineers", data),
};

export const scheduleApi = {
  get: (start, end) => api.get("/schedule", { params: { start, end } }),
  listInspections: () => api.get("/scheduled-inspections"),
  createEmergency: (payload) => api.post("/maintenance-jobs/emergency", payload),
  createInspection: (payload) => api.post("/scheduled-inspections", payload),
  cancelInspection: (sid) => api.delete(`/scheduled-inspections/${sid}`),
  updateInspection: (sid, data) => api.patch(`/scheduled-inspections/${sid}`, data),
};

export const materialsApi = {
  list: () => api.get("/materials-library"),
};

export const externalReportsApi = {
  list: (params) => api.get("/external-reports", { params }),
  getOne: (id, includeFile = false) =>
    api.get(`/external-reports/${id}`, { params: { include_file: includeFile } }),
  upload: (formData) =>
    api.post("/external-reports", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }),
  patch: (id, data) => api.patch(`/external-reports/${id}`, data),
  remove: (id) => api.delete(`/external-reports/${id}`),
  promote: (id, data = {}) => api.post(`/external-reports/${id}/promote`, data),
  generateQuote: (id, data = {}) => api.post(`/external-reports/${id}/generate-quote`, data),
  createQuote: (id) => api.post(`/external-reports/${id}/create-quote`),
};

export const plansApi = {
  list: (buildingId) => api.get(`/buildings/${buildingId}/plans`),
  getOne: (buildingId, planId) => api.get(`/buildings/${buildingId}/plans/${planId}`),
  create: (buildingId, data) => api.post(`/buildings/${buildingId}/plans`, data),
  patch: (buildingId, planId, data) => api.patch(`/buildings/${buildingId}/plans/${planId}`, data),
  remove: (buildingId, planId) => api.delete(`/buildings/${buildingId}/plans/${planId}`),
  fileUrl: (buildingId, planId) => `${API_BASE}/buildings/${buildingId}/plans/${planId}/file`,
};

export const exportsApi = {
  // exports.py — binary xlsx (StreamingResponse)
  quotesXlsx: () => api.get("/exports/quotes.xlsx", { responseType: "blob" }),
  maintenanceXlsx: () => api.get("/exports/maintenance-jobs.xlsx", { responseType: "blob" }),
  inspectionsXlsx: () => api.get("/exports/inspections.xlsx", { responseType: "blob" }),
  doorsXlsx: () => api.get("/exports/doors.xlsx", { responseType: "blob" }),
  allXlsx: () => api.get("/exports/all.xlsx", { responseType: "blob" }),
  // exports_all.py — JSON base64 envelope { filename, content_b64, mime }
  buildingDoorsXlsx: (bid) => api.get(`/buildings/${bid}/doors/export/xlsx`),
  buildingInspectionsXlsx: (bid) => api.get(`/buildings/${bid}/inspections/export/xlsx`),
  remedialSchedule: (bid, rows, format) => api.post(`/buildings/${bid}/remedial-schedule/export`, { rows }, { params: { format } }),
};

export const labelTemplateApi = {
  get: () => api.get("/settings/label-template"),
  update: (data) => api.put("/settings/label-template", data),
};

export const clientPortalApi = {
  listMaintenanceJobs: () => api.get("/client-portal/maintenance-jobs"),
  listScheduledInspections: () => api.get("/client-portal/scheduled-inspections"),
  listOwnInspectionRequests: () => api.get("/client-portal/inspection-requests"),
  requestInspection: (data) => api.post("/client-portal/inspection-requests", data),
  reportDoorFailure: (data) => api.post("/client-portal/door-failures", data),
  getMaintenanceJob: (id) => api.get(`/client-portal/maintenance-jobs/${id}`),
  uploadExternalReport: (data) => api.post("/client-portal/external-reports", data),
};

// Staff-side inbox of client-submitted survey requests.
export const inspectionRequestsApi = {
  list: (status) => api.get("/inspection-requests", { params: status ? { status } : {} }),
  attachment: (id, idx) => api.get(`/inspection-requests/${id}/attachments/${idx}`),
  resolve: (id, data) => api.patch(`/inspection-requests/${id}`, data),
  convertToScheduled: (id, data) => api.post(`/inspection-requests/${id}/convert-to-scheduled`, data),
};

export const marketplaceApi = {
  getConfig: () => api.get("/marketplace/config"),
  updateConfig: (data) => api.put("/marketplace/config", data),
  commissionPreview: (value) => api.post("/marketplace/commission/preview", { value }),
  listContractors: (params) => api.get("/marketplace/contractors", { params }),
  createContractor: (data) => api.post("/marketplace/contractors", data),
  updateContractor: (id, data) => api.put(`/marketplace/contractors/${id}`, data),
  removeContractor: (id) => api.delete(`/marketplace/contractors/${id}`),
  listLeads: (params) => api.get("/marketplace/leads", { params }),
  getLead: (id) => api.get(`/marketplace/leads/${id}`),
  leadFromQuote: (quoteId) => api.post(`/marketplace/leads/from-quote/${quoteId}`),
  leadDecision: (id, decision, note) => api.post(`/marketplace/leads/${id}/decision`, { decision, note }),
  me: () => api.get("/marketplace/me"),
  updateMe: (data) => api.put("/marketplace/me", data),
  submitMe: () => api.post("/marketplace/me/submit"),
  uploadDocument: (data) => api.post("/marketplace/me/documents", data),
  alerts: () => api.get("/marketplace/alerts"),
  // Phase 3 — bidding, award, commission
  availableLeads: () => api.get("/marketplace/available-leads"),
  submitBid: (leadId, data) => api.post(`/marketplace/leads/${leadId}/bids`, data),
  withdrawBid: (leadId) => api.delete(`/marketplace/leads/${leadId}/bids/me`),
  myBids: () => api.get("/marketplace/my-bids"),
  myPerformance: () => api.get("/marketplace/my-performance"),
  portalLeads: () => api.get("/marketplace/portal/leads"),
  awardBid: (leadId, bidId) => api.post(`/marketplace/portal/leads/${leadId}/award`, { bid_id: bidId }),
  listCharges: () => api.get("/marketplace/commission-charges"),
  updateCharge: (id, status) => api.put(`/marketplace/commission-charges/${id}`, { status }),
  // Phase 4 — territory subscriptions (RevenueCat)
  mySubscription: () => api.get("/marketplace/me/subscription"),
  syncSubscription: (data) => api.post("/marketplace/me/subscription/sync", data),
  territorySubscriptions: () => api.get("/marketplace/territory-subscriptions"),
};

// Client & portal management (admin/inspector). Mirrors the mobile
// clientsApi so the web app can manage client portals at parity.
export const clientsApi = {
  list: () => api.get("/clients"),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post("/clients", data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  remove: (id) => api.delete(`/clients/${id}`),
  invite: (id) => api.post(`/clients/${id}/invite`, {}),
  listPortalUsers: (id) => api.get(`/clients/${id}/portal-users`),
  removePortalUser: (id, userId) => api.delete(`/clients/${id}/portal-users/${userId}`),
  impersonate: (id) => api.post(`/clients/${id}/impersonate`, {}),
  sendStatement: (id) => api.post(`/clients/${id}/statement`, {}),
  // Iter-253 — buildings linked to a client (whole-building + door-level).
  buildings: (id) => api.get(`/clients/${id}/buildings`),
};

export default api;
